import { useEffect, useState, useMemo, useRef } from 'react';
import { userAppContext } from '../../../../../Context/Context.jsx';

import TableColumnsMenu from '../menu/TableColumnsMenu';
import { EVIDENCE_LEGEND } from '../../constants';

import RenderInfoLogic from '../../../../../Components/InfoSidebar/RenderInfoLogic.jsx';
import Grid from '../../../../../Components/Table/Grid.jsx';
import { Columns } from '../../../../../Components/Table/Columns.jsx';
import { ColumnVisibilityModels } from '../../../../../Components/Table/ColumnVisibilityModels';

import RenderSourcesLogic from '../source/RenderSourcesLogic';
import ScoreModal from '../modal/ScoreModal';

import SharedButton from '../../../../../global/Button/Button';
import { AdjustmentsHorizontalIcon, ViewColumnsIcon } from '@heroicons/react/16/solid';
import { ArrowDownTrayIcon, BookmarkIcon } from '@heroicons/react/16/solid';

import { DOWNLOAD_EXTENSIONS } from '../../constants';
import { fetchDownloadFiltersFile } from '../../apiCalls';

import { ColumnVisibility, ExtensionFile, UserContext } from '../../../../../types';
import SharedDialog from '../../../../../global/Modal/Modal';
import Alert from '../../../../../global/Alert/Alert';
import { isSourceEnabledForTab } from '../../helpers/sourceListHelpers';
import ErrorMessage from '../../../../../Components/UI/ErrorMessage.jsx';
import { EvidenceParams, evidenceSummaryRequest } from '../../helpers/tableHelpers';

import { buildFiltersObject } from '../../helpers/buildFiltersObject';
import BaseTooltip from '../../../../../global/Tooltip/BaseTooltip';
import { saveFilter } from '../../../../../services/savedFiltersService';
import { useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';

const SHOW_SUMMARIZE = process.env.REACT_APP_SHOW_SUMMARIZE === 'true';
const SHOW_SCORE_BUTTON = process.env.REACT_APP_SHOW_SCORE_BUTTON === 'true';

function ManageGrid(props) {
  const {
    isMapping,
    mappingContent,
    restrictChemical,
    view,
    tab,
    isEvidence,
    source,
    searchItems,
    setSearchItems,
    isInfoSidebarOpen,
    setIsInfoSidebarOpen,
    dispatch,
    defaultFilterValue,
  } = props;

  const { authorization, isAuthenticated, selectedInfoItem, restrictSources, user } =
    userAppContext() as unknown as UserContext;

  const useCustomColumnVisibility = () => {
    const selectColumnVisibilityModel = useMemo(() => {
      const fnColumnVisibilityModel =
        isMapping && mappingContent?.isActive
          ? () => ColumnVisibilityModels.getByMappingName(mappingContent.columVisibilityModelKey, isEvidence)
          : () => ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);

      const fnColumnVisibilityModelWithRestrictions = () => {
        const originalList = fnColumnVisibilityModel();
        const restrictedList = restrictChemical
          ? ['gof_lof', 'chemsInSentence', 'chemID', 'chemIDs', 'chemical_effect']
          : [];
        return originalList && restrictedList
          ? Object.fromEntries(Object.entries(originalList).filter(([key]) => !restrictedList.includes(key)))
          : undefined;
      };

      return fnColumnVisibilityModelWithRestrictions;
    }, [view, tab, mappingContent?.columVisibilityModelKey, restrictChemical, isMapping, isEvidence]);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
      return selectColumnVisibilityModel();
    });

    const [columnOrder, setColumnOrder] = useState<string[] | undefined>(undefined);

    useEffect(() => {
      const newBaseModel = selectColumnVisibilityModel();
      setColumnVisibilityModel(newBaseModel);
    }, [selectColumnVisibilityModel]);

    return { columnVisibilityModel, setColumnVisibilityModel, columnOrder, setColumnOrder };
  };

  const { columnVisibilityModel, setColumnVisibilityModel, columnOrder, setColumnOrder } =
    useCustomColumnVisibility();

  const [totalResults, setTotalResults] = useState(0);
  const [isColumnSelectOpen, setIsColumnSelectOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem('tableColumnOpen');
    return saved === 'true';
  });
  const [sourceOpen, setSourceOpen] = useState(false);
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [sourceWeights, setSourceWeights] = useState<Array<{ name: string; score: number }>>([]);
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const [pendingDownloadExt, setPendingDownloadExt] = useState<ExtensionFile | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState<'info' | 'success' | 'error' | 'warning'>('success');
  const [customAlertMessage, setCustomAlertMessage] = useState<string | null>(null);
  const isInvalidSource = source && !isSourceEnabledForTab(source, tab, restrictSources);
  const isAcademic = Array.isArray(user?.['cognito:groups']) && user['cognito:groups'][0] === 'ROLE_ACADEMIC';

  const [areEvidenceSelectedRows, setEvidenceSelectedRows] = useState(false);
  const [evidenceSummParams, setEvidenceSummParams] = useState<EvidenceParams[]>([]);
  const [isSavingSearch, setIsSavingSearch] = useState(false);
  const [isSavingColumns, setIsSavingColumns] = useState(false);
  const [showSavedColumns, setShowSavedColumns] = useState(false);
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [searchDataToSave, setSearchDataToSave] = useState<any>(null);
  const location = useLocation();

  const [showSaveColumnsModal, setShowSaveColumnsModal] = useState(false);
  const [columnSelectionName, setColumnSelectionName] = useState('');
  const [columnDataToSave, setColumnDataToSave] = useState<any>(null);

  const handleOpenMenus = (menu: string) => {
    setIsColumnSelectOpen(prev => (menu === 'columns' ? !prev : false));
    setSourceOpen(prev => (menu === 'source' ? !prev : false));

    // Score modal
    if (menu === 'score') {
      setScoreModalOpen(true);
    }
  };

  useEffect(() => {
    localStorage.setItem('tableColumnOpen', isColumnSelectOpen ? 'true' : 'false');
  }, [isColumnSelectOpen, tab]);

  const handleDataFromChild = (data: boolean, params: EvidenceParams[]) => {
    setEvidenceSelectedRows(data);
    setEvidenceSummParams(params);
  };

  const handleEvidenceSummClick = async () => {
    const content = await evidenceSummaryRequest(evidenceSummParams, dispatch);
    dispatch({
      type: 'SET_EVIDENCE_MODAL_DATA',
      payload: evidenceSummParams,
    });
    await dispatch({ type: 'SET_EVIDENCE_MODAL_CONTENT', payload: content });
    dispatch({ type: 'SET_SHOW_EVIDENCE_MODAL', payload: true });
  };

  const getFilterValueRef = useRef<any>(null);

  const alertMessage = () => {
    if (customAlertMessage) return customAlertMessage;

    if (alertType === 'success') {
      if (isSavingSearch) return 'Search saved successfully!';
      if (isSavingColumns) return 'Column selection saved successfully!';
      if (showSavedColumns) return 'Column selection loaded successfully!';
      if (isSavingSearch) return 'Search saved successfully!';
      if (isSavingColumns) return 'Column selection saved successfully!';
      if (showSavedColumns) return 'Column selection loaded successfully!';
      return 'Successfully downloaded!';
    }
    if (alertType === 'error') {
      if (isSavingSearch) return 'Error saving search. Please try again.';
      if (isSavingColumns) return 'Error saving column selection. Please try again.';
      if (isSavingSearch) return 'Error saving search. Please try again.';
      if (isSavingColumns) return 'Error saving column selection. Please try again.';
      return 'Error downloading file.';
    }
    if (alertType === 'warning') {
      return 'Download is being processed.';
    }
  };

  const handleSaveSearch = () => {
    if (!isAuthenticated) {
      setAlertType('error');
      setShowAlert(true);
      return;
    }

    const columnFilters = getFilterValueRef.current ? getFilterValueRef.current() : [];

    const urlParams = new URLSearchParams(location.search);
    const searchQuery = `view=${view}&tab=${tab}&source=${source}&idents=${urlParams.get('idents') || 'ALL'}`;

    const searchItemsText =
      searchItems && searchItems.length > 0
        ? searchItems.map(item => item.name || item.id).join(', ')
        : 'Search';

    const defaultSearchName = `${searchItemsText} - ${titleGrid} (${new Date().toLocaleDateString()})`;

    setSearchDataToSave({
      columnFilters,
      searchQuery,
      isEvidence,
      searchItems: searchItems || [],
      view,
      tab,
      source,
      totalResults,
    });
    setSearchName(defaultSearchName);

    setShowSaveSearchModal(true);
  };

  const handleConfirmSaveSearch = async () => {
    if (!searchDataToSave || !searchName.trim() || isSavingSearch) return;

    setIsSavingSearch(true);

    try {
      await saveFilter(
        searchName.trim(),
        searchDataToSave.columnFilters,
        searchDataToSave.isEvidence,
        searchDataToSave.searchQuery,
      );

      setAlertType('success');
      setShowAlert(true);
      setShowSaveSearchModal(false);
      setSearchDataToSave(null);
      setSearchName('');

      setTimeout(() => {
        setIsSavingSearch(false);
      }, 100);
    } catch (error) {
      console.error('Error saving search:', error);
      setAlertType('error');
      setShowAlert(true);
    } finally {
      setIsSavingSearch(false);
    }
  };

  const handleSaveColumnSelection = () => {
    if (!columnVisibilityModel) return;

    const searchItemsText =
      searchItems && searchItems.length > 0
        ? searchItems.map(item => item.name || item.id).join(', ')
        : 'Columns';

    const defaultColumnName = `${searchItemsText} - ${view} - ${tab} ${isEvidence ? '(Evidence)' : '(Summary)'} (${new Date().toLocaleDateString()})`;

    setColumnDataToSave({
      columnVisibilityModel,
      columnOrder,
      view,
      tab,
      isEvidence,
      searchItems: searchItems?.map(item => ({ id: item.id, name: item.name })),
    });
    setColumnSelectionName(defaultColumnName);
    setShowSaveColumnsModal(true);
  };

  const handleConfirmSaveColumnSelection = () => {
    if (!columnDataToSave || !columnSelectionName.trim() || isSavingColumns) return;

    setIsSavingColumns(true);

    try {
      const timestamp = new Date().getTime();
      const columnSelectionKey = `columnSelection_${timestamp}`;

      const columnSelection = {
        name: columnSelectionName.trim(),
        columnVisibilityModel: columnDataToSave.columnVisibilityModel,
        columnOrder: columnDataToSave.columnOrder,
        savedAt: new Date().toISOString(),
        searchContext: {
          view: columnDataToSave.view,
          tab: columnDataToSave.tab,
          isEvidence: columnDataToSave.isEvidence,
          searchItems: columnDataToSave.searchItems,
        },
      };

      localStorage.setItem(columnSelectionKey, JSON.stringify(columnSelection));

      setAlertType('success');
      setShowAlert(true);
      setShowSaveColumnsModal(false);
      setColumnDataToSave(null);
      setColumnSelectionName('');

      setTimeout(() => {
        setIsSavingColumns(false);
      }, 100);
    } catch (error) {
      console.error('Error saving column selection:', error);
      setAlertType('error');
      setShowAlert(true);
      setIsSavingColumns(false);
    }
  };

  const getSavedColumnSelections = () => {
    const savedSelections = [] as Array<{
      key: string;
      name: string;
      columnVisibilityModel: ColumnVisibility;
      columnOrder: string[];
      savedAt: string;
      searchContext: {
        view: string;
        tab: string;
        isEvidence: boolean;
        searchItems: Array<{ id: string; name: string }>;
      };
    }>;
    const keys = Object.keys(localStorage);

    keys.forEach(key => {
      if (key.startsWith('columnSelection_')) {
        try {
          const selection = JSON.parse(localStorage.getItem(key) || '');
          savedSelections.push({
            key,
            ...selection,
          });
        } catch (error) {
          console.error('Error parsing saved column selection:', error);
        }
      }
    });

    return savedSelections.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
  };

  const handleLoadColumnSelection = (selection: any) => {
    if (selection.columnVisibilityModel) {
      setColumnVisibilityModel(selection.columnVisibilityModel);
    }
    if (selection.columnOrder) {
      setColumnOrder(selection.columnOrder);
    }

    setAlertType('success');
    setShowAlert(true);
  };

  const performDownloadWithFilters = async (extension: ExtensionFile) => {
    try {
      setAlertType('warning');
      setShowAlert(true);
      dispatch({ type: 'SET_LOADING', payload: true });

      const filterValue = getFilterValueRef.current ? getFilterValueRef.current() : [];

      await fetchDownloadFiltersFile({
        extension: extension.value,
        columnVisibilityModel: columnVisibilityModel as ColumnVisibility,
        view,
        source,
        tab,
        searchItems,
        titleGrid,
        isAuthenticated,
        authorization,
        isEvidence,
        isMapping,
        mappingContent,
        filters: buildFiltersObject(filterValue),
        filterValue,
        totalResults,
      });
    } catch (error) {
      setAlertType('error');
      setShowAlert(true);
      console.error('Error downloading file:', error);
    } finally {
      setAlertType('success');
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleDownloadFilesWithFilters = async (extension: ExtensionFile) => {
    setPendingDownloadExt(extension);

    if (searchItems && searchItems.length > 50) {
      setCustomAlertMessage('Cannot download more than 50 search items. Please reduce your selection.');
      setAlertType('error');
      setShowAlert(true);
      return;
    }

    if (totalResults > 55000) {
      setShowDownloadConfirm(true);
      setPendingDownloadExt(extension);
      return;
    }

    await performDownloadWithFilters(extension);
  };

  const titleGrid = isEvidence ? 'Evidence' : 'Summary';

  // Memoized columns based on columnVisibilityModel
  const columns = useMemo(() => {
    return columnVisibilityModel
      ? Object.keys(columnVisibilityModel).flatMap(key =>
          Columns().filter(
            column =>
              key === column.name &&
              columnVisibilityModel[column.name] === true &&
              (column.show === undefined || column.show === true),
          ),
        )
      : null;
  }, [columnVisibilityModel]);

  useEffect(() => {
    const searchAndMappingItems = searchItems ? searchItems.concat(mappingContent?.mappingItems) : [];
    if (isInfoSidebarOpen && searchItems && !searchItems.find(item => item.id === selectedInfoItem.id)) {
      if (!searchAndMappingItems.find(item => item.id === selectedInfoItem.id)) {
        setIsInfoSidebarOpen(false);
      }
    }
  }, [searchItems, selectedInfoItem, mappingContent?.mappingItems, isInfoSidebarOpen, setIsInfoSidebarOpen]);

  const activeFilters = searchDataToSave?.columnFilters?.filter(f => f.value && f.value.length !== 0) || [];

  if (isAcademic && isAuthenticated && tab === 'CHEMICALS') {
    return (
      <ErrorMessage
        title="This content is not available with your current plan"
        subtitle={
          <div className="text-center">
            <p>Upgrade your plan to access this feature.</p>
            <span>
              <button className="btn-gradient-pink">
                <a href="/Profile-area" className="text-white">
                  Upgrade your plan
                </a>
              </button>
            </span>
          </div>
        }
      />
    );
  }

  return !columns ? null : (
    <>
      {showAlert && (
        <div
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
          }}
        >
          <Alert
            message={alertMessage() as string}
            type={alertType}
            onClose={() => {
              setShowAlert(false);
              setCustomAlertMessage(null);
            }}
          />
        </div>
      )}
      {showDownloadConfirm && (
        <SharedDialog
          isOpen={showDownloadConfirm}
          onClose={() => setShowDownloadConfirm(false)}
          title="Large file download warning"
          description="The file you are requesting contains more than 55,000 records. Downloads are limited to this number. We suggest using the REST API for this request."
          confirmText="Yes"
          cancelText="No"
          hasConfirmBtn
          onConfirm={async () => {
            if (pendingDownloadExt) {
              await performDownloadWithFilters(pendingDownloadExt);
              setPendingDownloadExt(null);
            }
            setShowDownloadConfirm(false);
          }}
          data-testid="modal"
        >
          <p className="font-medium text-gray-900 mt-2">Do you want to proceed?</p>
        </SharedDialog>
      )}

      {/* Columns modal */}
      {showSaveColumnsModal && (
        <SharedDialog
          isOpen={showSaveColumnsModal}
          onClose={() => {
            setShowSaveColumnsModal(false);
            setColumnDataToSave(null);
            setColumnSelectionName('');
          }}
          title="Save Column Selection"
          description="Give your column selection a name to save it for later use."
          confirmText={isSavingColumns ? 'Saving...' : 'Save'}
          cancelText="Cancel"
          onConfirm={handleConfirmSaveColumnSelection}
          data-testid="save-columns-modal"
          hasConfirmBtn
        >
          <div className="mt-4">
            <div className="mb-3">
              <label htmlFor="columnSelectionName" className="form-label font-medium">
                Column Selection Name:
              </label>
              <input
                id="columnSelectionName"
                type="text"
                className="form-control"
                value={columnSelectionName}
                onChange={e => setColumnSelectionName(e.target.value)}
                placeholder="Enter a name for this column selection"
                maxLength={100}
                disabled={isSavingColumns}
              />
              {!columnSelectionName.trim() && (
                <small className="text-danger">Please enter a name for the column selection</small>
              )}
            </div>

            {columnDataToSave && (
              <div className="mt-3">
                <h6 className="font-medium mb-2">Selection Details:</h6>
                <div className="bg-light p-3 rounded">
                  <div className="row">
                    <div className="col-md-6">
                      <small className="text-muted">View:</small> <strong>{columnDataToSave.view}</strong>
                      <br />
                      <small className="text-muted">Tab:</small> <strong>{columnDataToSave.tab}</strong>
                      <br />
                      <small className="text-muted">Type:</small>{' '}
                      <strong>{columnDataToSave.isEvidence ? 'Evidence' : 'Summary'}</strong>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Search Items:</small>
                      <br />
                      {columnDataToSave.searchItems && columnDataToSave.searchItems.length > 0 ? (
                        <small>
                          {columnDataToSave.searchItems.map(item => item.name || item.id).join(', ')}
                        </small>
                      ) : (
                        <small className="text-muted">No specific items</small>
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <small className="text-muted">Visible Columns:</small>
                    <br />
                    <small>
                      {Object.entries(columnDataToSave.columnVisibilityModel || {})
                        .filter(([_, visible]) => visible)
                        .map(([col]) => col)
                        .join(', ')}
                    </small>
                  </div>
                </div>
              </div>
            )}
          </div>
        </SharedDialog>
      )}

      {/* Score Modal */}
      <ScoreModal
        isOpen={scoreModalOpen}
        onClose={() => setScoreModalOpen(false)}
        onApplyWeights={setSourceWeights}
      />

      {/* Save Search Modal */}
      {showSaveSearchModal && (
        <SharedDialog
          isOpen={showSaveSearchModal}
          onClose={() => {
            setShowSaveSearchModal(false);
            setSearchDataToSave(null);
            setSearchName('');
          }}
          title="Save Search"
          description="Review and customize your search before saving it to favorites."
          confirmText={isSavingSearch ? 'Saving...' : 'Save Search'}
          cancelText="Cancel"
          onConfirm={handleConfirmSaveSearch}
          data-testid="save-search-modal"
          hasConfirmBtn
        >
          <div className="mt-4">
            <div className="mb-3">
              <label htmlFor="searchName" className="form-label font-medium">
                Search Name:
              </label>
              <input
                id="searchName"
                type="text"
                className="form-control"
                value={searchName}
                onChange={e => setSearchName(e.target.value)}
                placeholder="Enter a name for this search"
                maxLength={100}
                disabled={isSavingSearch}
              />
              {!searchName.trim() && (
                <small className="text-danger">Please enter a name for the search</small>
              )}
            </div>

            {searchDataToSave && (
              <div className="mt-3">
                <h6 className="font-medium mb-2">Search Details:</h6>
                <div className="bg-light p-3 rounded">
                  <div className="row">
                    <div className="col-md-6">
                      <small className="text-muted">View:</small> <strong>{searchDataToSave.view}</strong>
                      <br />
                      <small className="text-muted">Tab:</small> <strong>{searchDataToSave.tab}</strong>
                      <br />
                      <small className="text-muted">Source:</small> <strong>{searchDataToSave.source}</strong>
                      <br />
                      <small className="text-muted">Type:</small>{' '}
                      <strong>{searchDataToSave.isEvidence ? 'Evidence' : 'Summary'}</strong>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Search Items:</small>
                      <br />
                      {searchDataToSave.searchItems.length > 0 ? (
                        <small>
                          {searchDataToSave.searchItems.map(item => item.name || item.id).join(', ')}
                        </small>
                      ) : (
                        <small className="text-muted">No specific items</small>
                      )}
                    </div>
                  </div>

                  <div className="mt-2">
                    <small className="text-muted">Applied Column Filters:</small>
                    <br />
                    {activeFilters.length > 0 ? (
                      <small>
                        {activeFilters
                          .map(
                            f =>
                              `${f.label || f.name}: ${Array.isArray(f.value) ? f.value.join(', ') : f.value}`,
                          )
                          .join(', ')}
                      </small>
                    ) : (
                      <small className="text-muted">No column filters applied</small>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </SharedDialog>
      )}
      {/* Save Search Modal */}
      {showSaveSearchModal && (
        <SharedDialog
          isOpen={showSaveSearchModal}
          onClose={() => {
            setShowSaveSearchModal(false);
            setSearchDataToSave(null);
            setSearchName('');
          }}
          title="Save Search"
          description="Review and customize your search before saving it to favorites."
          confirmText={isSavingSearch ? 'Saving...' : 'Save Search'}
          cancelText="Cancel"
          onConfirm={handleConfirmSaveSearch}
          data-testid="save-search-modal"
          hasConfirmBtn
        >
          <div className="mt-4">
            <div className="mb-3">
              <label htmlFor="searchName" className="form-label font-medium">
                Search Name:
              </label>
              <input
                id="searchName"
                type="text"
                className="form-control"
                value={searchName}
                onChange={e => setSearchName(e.target.value)}
                placeholder="Enter a name for this search"
                maxLength={100}
                disabled={isSavingSearch}
              />
              {!searchName.trim() && (
                <small className="text-danger">Please enter a name for the search</small>
              )}
            </div>

            {searchDataToSave && (
              <div className="mt-3">
                <h6 className="font-medium mb-2">Search Details:</h6>
                <div className="bg-light p-3 rounded">
                  <div className="row">
                    <div className="col-md-6">
                      <small className="text-muted">View:</small> <strong>{searchDataToSave.view}</strong>
                      <br />
                      <small className="text-muted">Tab:</small> <strong>{searchDataToSave.tab}</strong>
                      <br />
                      <small className="text-muted">Source:</small> <strong>{searchDataToSave.source}</strong>
                      <br />
                      <small className="text-muted">Type:</small>{' '}
                      <strong>{searchDataToSave.isEvidence ? 'Evidence' : 'Summary'}</strong>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Search Items:</small>
                      <br />
                      {searchDataToSave.searchItems.length > 0 ? (
                        <small>
                          {searchDataToSave.searchItems.map(item => item.name || item.id).join(', ')}
                        </small>
                      ) : (
                        <small className="text-muted">No specific items</small>
                      )}
                    </div>
                  </div>

                  <div className="mt-2">
                    <small className="text-muted">Applied Column Filters:</small>
                    <br />
                    {activeFilters.length > 0 ? (
                      <small>
                        {activeFilters
                          .map(
                            f =>
                              `${f.label || f.name}: ${Array.isArray(f.value) ? f.value.join(', ') : f.value}`,
                          )
                          .join(', ')}
                      </small>
                    ) : (
                      <small className="text-muted">No column filters applied</small>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </SharedDialog>
      )}
      {/* Columns modal */}
      {showSaveColumnsModal && (
        <SharedDialog
          isOpen={showSaveColumnsModal}
          onClose={() => {
            setShowSaveColumnsModal(false);
            setColumnDataToSave(null);
            setColumnSelectionName('');
          }}
          title="Save Column Selection"
          description="Give your column selection a name to save it for later use."
          confirmText={isSavingColumns ? 'Saving...' : 'Save'}
          cancelText="Cancel"
          onConfirm={handleConfirmSaveColumnSelection}
          data-testid="save-columns-modal"
          hasConfirmBtn
        >
          <div className="mt-4">
            <div className="mb-3">
              <label htmlFor="columnSelectionName" className="form-label font-medium">
                Column Selection Name:
              </label>
              <input
                id="columnSelectionName"
                type="text"
                className="form-control"
                value={columnSelectionName}
                onChange={e => setColumnSelectionName(e.target.value)}
                placeholder="Enter a name for this column selection"
                maxLength={100}
                disabled={isSavingColumns}
              />
              {!columnSelectionName.trim() && (
                <small className="text-danger">Please enter a name for the column selection</small>
              )}
            </div>

            {columnDataToSave && (
              <div className="mt-3">
                <h6 className="font-medium mb-2">Selection Details:</h6>
                <div className="bg-light p-3 rounded">
                  <div className="row">
                    <div className="col-md-6">
                      <small className="text-muted">View:</small> <strong>{columnDataToSave.view}</strong>
                      <br />
                      <small className="text-muted">Tab:</small> <strong>{columnDataToSave.tab}</strong>
                      <br />
                      <small className="text-muted">Type:</small>{' '}
                      <strong>{columnDataToSave.isEvidence ? 'Evidence' : 'Summary'}</strong>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Search Items:</small>
                      <br />
                      {columnDataToSave.searchItems && columnDataToSave.searchItems.length > 0 ? (
                        <small>
                          {columnDataToSave.searchItems.map(item => item.name || item.id).join(', ')}
                        </small>
                      ) : (
                        <small className="text-muted">No specific items</small>
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <small className="text-muted">Visible Columns:</small>
                    <br />
                    <small>
                      {Object.entries(columnDataToSave.columnVisibilityModel || {})
                        .filter(([_, visible]) => visible)
                        .map(([col]) => col)
                        .join(', ')}
                    </small>
                  </div>
                </div>
              </div>
            )}
          </div>
        </SharedDialog>
      )}
      <div className="Cabtabla bg-white mt-3">
        {searchItems && (
          <>
            <div className="row">
              <RenderInfoLogic
                searchItems={searchItems}
                selectedInfoItem={selectedInfoItem}
                setSearchItems={setSearchItems}
                isMapping={isMapping}
              />
              <div className="col-6 text-right">
                <div className="row justify-content-end items-center">
                  {isAuthenticated &&
                    !isEmpty(searchItems) &&
                    !isAcademic &&
                    process.env.REACT_APP_SHOW_SAVE_SEARCHES === 'true' && (
                      <>
                        <SharedButton onClick={handleSaveSearch} disabled={isSavingSearch} className="mt-2">
                          <BookmarkIcon className="w-5 mr-2" />
                          {isSavingSearch ? 'Saving...' : 'Save Search'}
                        </SharedButton>
                      </>
                    )}
                  <SharedButton
                    onClick={() => handleOpenMenus('columns')}
                    arrow={isColumnSelectOpen ? 'up' : 'down'}
                    className="mt-2"
                  >
                    <ViewColumnsIcon className="w-5 mr-2" />
                    Select Columns
                  </SharedButton>
                  <SharedButton
                    onClick={() => handleOpenMenus('source')}
                    arrow={sourceOpen ? 'up' : 'down'}
                    className="mt-2"
                  >
                    Source: {source.replace('_', ' ')}
                  </SharedButton>
                  {SHOW_SCORE_BUTTON && !isAcademic && (
                    <SharedButton onClick={() => handleOpenMenus('score')} className="mt-2">
                      <AdjustmentsHorizontalIcon className="w-5 mr-2" />
                      Score
                    </SharedButton>
                  )}
                </div>
                {process.env.REACT_APP_SHOW_COLUMNS_SELECTION === 'true' && (
                  <div className="justify-content-end mt-2">
                    <SharedButton
                      onClick={handleSaveColumnSelection}
                      disabled={isSavingColumns}
                      className="mr-2"
                      variant="outline"
                      size="sm"
                    >
                      {isSavingColumns ? 'Saving...' : 'Save Column Selection'}
                    </SharedButton>
                    <SharedButton
                      onClick={() => setShowSavedColumns(!showSavedColumns)}
                      variant="outline"
                      size="sm"
                      arrow={showSavedColumns ? 'up' : 'down'}
                    >
                      Load Column Selection
                    </SharedButton>
                  </div>
                )}
                <div className="justify-content-end mt-2">
                  {view !== 'ALL' &&
                    DOWNLOAD_EXTENSIONS.map((extension, index) => (
                      <>
                        <BaseTooltip
                          key={extension.value || index}
                          content={`This will download a zip with search info a result`}
                          position="bottom"
                          contentClassName="max-w-xl"
                          style={{ whiteSpace: 'break-spaces', width: '15em' }}
                        >
                          <SharedButton onClick={() => handleDownloadFilesWithFilters(extension)}>
                            {extension.label}
                            <ArrowDownTrayIcon className="w-4 ml-2" />
                          </SharedButton>
                        </BaseTooltip>
                      </>
                    ))}
                </div>
              </div>
              <div>
                {sourceOpen && <RenderSourcesLogic isScoreSource={false} />}
                {isAcademic && tab !== 'CHEMICALS' && sourceOpen && (
                  <ErrorMessage
                    title=""
                    subtitle={
                      <p style={{ fontSize: '18px', color: 'rgb(190,0,128)' }}>
                        You are viewing limited sources. Do you want to access more sources?
                        <a href="/academic-plans"> Upgrade</a> your account
                      </p>
                    }
                  />
                )}
              </div>
            </div>
            {isColumnSelectOpen && (
              <TableColumnsMenu
                view={view}
                tab={tab}
                setIsColumnSelectOpen={setIsColumnSelectOpen}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
              />
            )}
            {showSavedColumns && (
              <div className="mt-3 p-3 border rounded bg-light">
                <h6>Saved Column Selections</h6>
                {(() => {
                  const savedSelections = getSavedColumnSelections();
                  if (savedSelections.length === 0) {
                    return <p className="text-muted">No saved column selections found.</p>;
                  }
                  return (
                    <div className="row">
                      {savedSelections.map((selection, index) => (
                        <div key={selection.key} className="col-md-6 mb-2">
                          <div className="columns-card p-2">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong>{selection.name}</strong>
                                <br />{' '}
                                <small className="text-muted">
                                  {selection.searchContext?.view} - {selection.searchContext?.tab}
                                  {selection.searchContext?.isEvidence ? ' (Evidence)' : ' (Summary)'} -{' '}
                                  Saved: {new Date(selection.savedAt).toLocaleDateString()}
                                </small>
                              </div>
                              <div>
                                <SharedButton
                                  size="xs"
                                  onClick={() => handleLoadColumnSelection(selection)}
                                  className="mr-1"
                                >
                                  Load
                                </SharedButton>
                                <SharedButton
                                  size="xs"
                                  variant="danger"
                                  onClick={() => {
                                    localStorage.removeItem(selection.key);
                                    setShowSavedColumns(false);
                                    setTimeout(() => setShowSavedColumns(true), 100);
                                  }}
                                >
                                  Delete
                                </SharedButton>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
            <div className="titEtiqueta">
              <span>{titleGrid}</span>
            </div>
          </>
        )}
      </div>
      {isEvidence && (
        <div className="flex justify-between">
          {EVIDENCE_LEGEND}
          {areEvidenceSelectedRows && SHOW_SUMMARIZE && (
            <div>
              <SharedButton onClick={handleEvidenceSummClick}>Summarize</SharedButton>
            </div>
          )}
        </div>
      )}
      {isInvalidSource ? (
        <div className="text-center text-gray-500 italic p-6">
          <strong>{source}</strong> is not a source for <strong>{tab}</strong>
        </div>
      ) : (
        <Grid
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          columnOrder={columnOrder}
          setColumnOrder={setColumnOrder}
          view={view}
          source={source}
          tab={tab}
          searchItems={searchItems}
          isEvidence={isEvidence}
          isMapping={isMapping}
          setTotalResults={setTotalResults}
          onData={handleDataFromChild}
          getFilterValueRef={getFilterValueRef}
          defaultFilterValue={defaultFilterValue}
          sourceWeights={sourceWeights}
        />
      )}
    </>
  );
}

export default ManageGrid;
